import React, { useState, useMemo, useEffect } from "react";
import ReactMapGL, { _MapContext as MapContext, ScaleControl, NavigationControl, Popup, Source, Layer, WebMercatorViewport, LinearInterpolator} from "react-map-gl";
import Map3Control from './Map3Control';
import mapboxgl from "mapbox-gl";
import bbox from '@turf/bbox';

//Components
import Logo from "../Logo";
import MapboxLogo from "../mapboxLogo";
import LegendCanopyCvr from "./LegendCanopyCvr";
import LegendBasalArea from "./LegendBasalArea";
import LegendBiomass from "./LegendBiomass";
import LegendForestType from "./LegendForestType";
import { fireboundHighlight } from "./Map3FireStyle";
import { MAPBOX_TOKEN } from "../../config";

//CSS
import "./Map3.css";

// eslint-disable-next-line import/no-webpack-loader-syntax
mapboxgl.workerClass = require("worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker").default;

const mapboxToken = MAPBOX_TOKEN

const Map3 = (props) => {
  const [mapStyle, setMapStyle] = useState("");
  const [switchState, setSwitchState] = useState(true);
  const [activeLayer, setActiveLayer] = useState("Canopy Cover");

  const [viewport, setViewport] = useState({
    latitude: 38.89,
    longitude: -98.47,
    zoom: 3.95,
    bearing: 0,
    pitch: 0,
  });

  const navControlStyle = {
    right: 10,
    top: 11,
  };

  const scaleControlStyle = {
    right: 10,
    bottom: 108,
  };

  const [fireInfo, setFireInfo] = useState(null);
  const [fireStat, setFireStat] = useState(undefined);
  const graphData = props.graphData;
  const setGraphData = props.setGraphData;
  const setOpen = props.setOpen;

  const onClick = event => {
    console.log("onClick Event", event)
    setGraphData({
      fireId: event.features[0] && event.features[0].properties.FID,
      canopyCoverMean16: event.features[0] && event.features[0].properties.mean_2016_canopycover,
      canopyCoverMean17: event.features[0] && event.features[0].properties.mean_2017_canopycover,
      canopyCoverMean18: event.features[0] && event.features[0].properties.mean_2018_canopycover,
      canopyCoverMean19: event.features[0] && event.features[0].properties.mean_2019_canopycover,
      canopyCoverMean20: event.features[0] && event.features[0].properties.mean_2020_canopycover,

      biomassSum16: event.features[0] && event.features[0].properties.mean_2016_biomass,
      biomassSum17: event.features[0] && event.features[0].properties.mean_2017_biomass,
      biomassSum18: event.features[0] && event.features[0].properties.mean_2018_biomass,
      biomassSum19: event.features[0] && event.features[0].properties.mean_2019_biomass,
      biomassSum20: event.features[0] && event.features[0].properties.mean_2020_biomass,

      basalMean16: event.features[0] && event.features[0].properties.mean_2016_basalarea,
      basalMean17: event.features[0] && event.features[0].properties.mean_2017_basalarea,
      basalMean18: event.features[0] && event.features[0].properties.mean_2018_basalarea,
      basalMean19: event.features[0] && event.features[0].properties.mean_2019_basalarea,
      basalMean20: event.features[0] && event.features[0].properties.mean_2020_basalarea,

    });
    setOpen(true);
    const feature = event.features[0];
    if (feature) {
      // calculate the bounding box of the feature
      const [minLng, minLat, maxLng, maxLat] = bbox(feature);
      // construct a viewport instance from the current state
      const vp = new WebMercatorViewport(viewport);
      const {longitude, latitude, zoom} = vp.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat]
        ],
        {
          padding: 200
        }
      );
      setViewport({
        ...viewport,
        longitude,
        latitude,
        zoom,
        transitionInterpolator: new LinearInterpolator({
          around: [event.offsetCenter.x, event.offsetCenter.y]
        }),
        transitionDuration: 1000
      });
    }
  }

  const selectedFire = (graphData && graphData.fireId) || '';
  const filter = useMemo(() => ['in', 'FID', selectedFire], [selectedFire]);

  const onHover = event => {
    setFireInfo({
      fireId: event.features[0] && event.features[0].properties.FID,
      longitude: event.lngLat[0],
      latitude: event.lngLat[1],
      year: event.features[0] && event.features[0].properties.FIRE_YEAR,
      incident: event.features[0] && event.features[0].properties.INCIDENT,
      acres: event.features[0] && event.features[0].properties.GIS_ACRES,
      agency: event.features[0] && event.features[0].properties.AGENCY
    })

    let fire = event.features[0]
    setFireStat(fire);
  };

  useEffect (() => {
  }, [fireStat, activeLayer, graphData])

  return (
    <>
      <Map3Control
        onChange={setMapStyle}
        switchState={switchState}
        setSwitchState={setSwitchState}
        activeLayer={activeLayer}
        setActiveLayer={setActiveLayer}
      />

      <ReactMapGL
        {...viewport}
        onViewportChange={setViewport}
        mapboxApiAccessToken={mapboxToken}
        mapStyle={mapStyle}
        height='100%'
        width='100%'
        onClick={onClick}
        onHover={onHover}
        interactiveLayerIds={[ 'Fire Boundaries Highlight', 'Corrected Biomass']}
      >
        <Source type="vector" tiles={["https://usfs.blob.core.windows.net/app/tiles_Fire_Region_Statistics_slimdata/{z}/{x}/{y}.pbf"]}>
          <Layer {...fireboundHighlight} filter={filter} />
        </Source>

        <NavigationControl style={navControlStyle} showCompass={false} />
        <ScaleControl
          maxWidth={100}
          unit="metric"
          style={scaleControlStyle}
          className="scaleControl"
        />

        {fireStat !== undefined && (
              <Popup
                longitude={fireInfo.longitude}
                latitude={fireInfo.latitude}
                closeButton={false}
                style={{backgroundColor: "white"}}
                >
                  <body>Year: {fireInfo.year}</body>
                  <body>Incident: {fireInfo.incident}</body>
                  <body>Acres burned: {fireInfo.acres}</body>
                  <body>Source: {fireInfo.agency}</body>
              </Popup>
         )} 

        <div className="flex-container">
          <div className="map3LegendStructure">
            {activeLayer === "Canopy Cover" && <LegendCanopyCvr />}
            {activeLayer === "Biomass" && <LegendBiomass />}
            {activeLayer === "Basal Area" && <LegendBasalArea />}
            {activeLayer === "Class" && <LegendForestType />}  
            {/* {switchState === true && <LegendForestDisturbance />} */}
          </div>
        </div>
      </ReactMapGL>

      <Logo className="fullScreenLogo" height={70} width={175} />

      <MapboxLogo />
    </>
  );
};

export default Map3